import { createUserWithEmailAndPassword, onAuthStateChanged, sendPasswordResetEmail, signInWithEmailAndPassword, signOut, type User } from 'firebase/auth'
import { auth } from '../../firebaseConfig'
import { normalizeEmail } from '@/utils/emailUtils'
import handleError from './util/ErrorHandler'
import type { ServiceError } from './util/ServiceError'
import { useProfileStore } from '@/store/profile.module'
import * as Sentry from '@sentry/vue'

class AuthService {
  private storeProfile: any
  private authUser: User | null = null
  private authInitialized = false
  private authInitPromise: Promise<User | null> | null = null

  async initAuth(): Promise<User | null> {
    if (this.authInitPromise) {
      return this.authInitPromise
    }

    this.authInitPromise = this.waitForAuthState().then((user) => {
      this.authUser = user
      this.authInitialized = true
      return user
    })

    return this.authInitPromise
  }

  private waitForAuthState(): Promise<User | null> {
    return new Promise((resolve, reject) => {
      const unsubscribe = onAuthStateChanged(
        auth,
        (user) => {
          unsubscribe()
          resolve(user)
        },
        reject
      )
    })
  }

  async login(user: string, password: string): Promise<User | ServiceError> {
    return signInWithEmailAndPassword(auth, normalizeEmail(user), password)
      .then((userCredential) => {
        const user = userCredential.user
        this.authUser = user
        this.authInitialized = true
        return user
      })
      .catch((error) => {
        const errorCode = error.code
        const errorMessage = error.message

        console.log('errorCode', errorCode)
        console.log('errorMessage', errorMessage)
        return handleError(error)
      })
  }

  async register(email: string, password: string): Promise<User | ServiceError> {
    return createUserWithEmailAndPassword(auth, normalizeEmail(email), password)
      .then((userCredential) => {
        const user = userCredential.user
        this.authUser = user
        this.authInitialized = true
        return user
      })
      .catch((error) => {
        const errorCode = error.code
        const errorMessage = error.message

        console.log('errorCode', errorCode)
        console.log('errorMessage', errorMessage)
        return handleError(error)
      })
  }

  async logout(): Promise<void> {
    this.storeProfile = useProfileStore()

    await this.storeProfile.deleteProfile()
    Sentry.setUser(null)
    this.authUser = null
    await signOut(auth)
  }

  async resetPassword(email: string): Promise<void | ServiceError> {
    return sendPasswordResetEmail(auth, normalizeEmail(email))
      .then(() => {
        return
      })
      .catch((error) => {
        const errorCode = error.code
        const errorMessage = error.message

        console.log('errorCode', errorCode)
        console.log('errorMessage', errorMessage)
        return handleError(error)
      })
  }

  getCurrentUser(): User | null {
    return this.authUser ?? auth.currentUser
  }

  async isAuthenticated(): Promise<boolean> {
    if (!this.authInitialized) {
      await this.initAuth()
    }

    return this.authUser !== null
  }
}

export const authService = new AuthService()
